import { useState, type ReactElement, type SyntheticEvent } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { authClient } from '../lib/auth-client.ts'
import {
  Button,
  Callout,
  Field,
  Input,
  PageHeader,
} from '../components/ui/index.ts'

export const Route = createFileRoute('/login')({ component: LoginPage })

function LoginPage(): ReactElement {
  const router = useRouter()
  const [mode, setMode] = useState<'sign-in' | 'sign-up'>('sign-in')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result =
        mode === 'sign-in'
          ? await authClient.signIn.email({ email, password })
          : await authClient.signUp.email({ email, password, name })
      if (result.error) {
        setError(result.error.message ?? 'Authentication failed')
        return
      }
      await router.navigate({ to: '/' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed')
    } finally {
      setBusy(false)
    }
  }

  const isSignUp = mode === 'sign-up'

  return (
    // A `<div>`, not a `<main>`: `AppShell` renders the page's one
    // `<main>` around this, and a nested second one is invalid markup and
    // a duplicate landmark.
    <div className="mx-auto max-w-sm p-8">
      <PageHeader title={isSignUp ? 'Create account' : 'Sign in'} />
      <form
        onSubmit={(e) => {
          void submit(e)
        }}
        className="flex flex-col gap-3"
      >
        {isSignUp && (
          // The placeholders are kept as well as the labels: three route
          // tests name these controls by placeholder, and a placeholder
          // that repeats its label costs nothing. The LABEL is what makes
          // them findable once the value is typed in, which a placeholder
          // never was.
          <Field label="Name" isRequired>
            {(control) => (
              <Input
                {...control}
                placeholder="Name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                }}
              />
            )}
          </Field>
        )}
        <Field label="Email" isRequired>
          {(control) => (
            <Input
              {...control}
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
            />
          )}
        </Field>
        <Field
          label="Password"
          hint="At least 8 characters."
          isRequired
          error={error}
        >
          {(control) => (
            <Input
              {...control}
              type="password"
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
              }}
              minLength={8}
            />
          )}
        </Field>
        {error !== null && (
          // Also a Callout, not only the Field error: a failure here is
          // usually about the account rather than the password box, and
          // `role="alert"` is what announces it to a screen reader.
          <Callout tone="danger" title="Authentication failed">
            {error}
          </Callout>
        )}
        <Button type="submit" variant="primary" isLoading={busy}>
          {isSignUp ? 'Sign up' : 'Sign in'}
        </Button>
      </form>
      <div className="mt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setMode(isSignUp ? 'sign-in' : 'sign-up')
            setError(null)
          }}
        >
          {isSignUp ? 'Have an account? Sign in' : 'No account? Sign up'}
        </Button>
      </div>
    </div>
  )
}
